import React, { useEffect, useMemo, useState } from "react";
import {
  Download,
  ExternalLink,
  FileArchive,
  FileCode,
  FileSpreadsheet,
  FileText,
  Search,
  Trash2,
} from "lucide-react";
import { apiService } from "../../service/apiClient";
import { apiUrl } from "../../service/apiUrl";
import DataTable from "../reuseable/DataTable";
import PaginationControls from "../reuseable/PaginationControls";
import TableSkeletonRow from "../reuseable/TableSkeletonRow";
import DeleteModal from "../modal/DeleteModal";

const parseFileList = (value) => {
  if (!value) {
    return [];
  }

  return String(value)
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const truncateText = (value, max = 40) => {
  const text = String(value || "");
  if (text.length <= max) {
    return text;
  }
  return `${text.slice(0, max - 1)}...`;
};

const getFileVisual = (fileName) => {
  const ext = String(fileName || "")
    .split(".")
    .pop()
    .toLowerCase();

  const spreadsheetExt = new Set(["xls", "xlsx", "csv"]);
  const wordExt = new Set(["doc", "docx"]);
  const slideExt = new Set(["ppt", "pptx"]);
  const archiveExt = new Set(["zip", "rar", "7z"]);
  const codeExt = new Set(["json", "xml", "html", "js", "ts", "py"]);

  if (ext === "pdf") {
    return {
      Icon: FileText,
      iconColor: "#dc2626",
      badgeBg: "#fee2e2",
      badgeText: "PDF",
    };
  }

  if (spreadsheetExt.has(ext)) {
    return {
      Icon: FileSpreadsheet,
      iconColor: "#15803d",
      badgeBg: "#dcfce7",
      badgeText: "XLS",
    };
  }

  if (wordExt.has(ext)) {
    return {
      Icon: FileText,
      iconColor: "#1d4ed8",
      badgeBg: "#dbeafe",
      badgeText: "DOC",
    };
  }

  if (slideExt.has(ext)) {
    return {
      Icon: FileText,
      iconColor: "#c2410c",
      badgeBg: "#ffedd5",
      badgeText: "PPT",
    };
  }

  if (ext === "txt") {
    return {
      Icon: FileText,
      iconColor: "#374151",
      badgeBg: "#f3f4f6",
      badgeText: "TXT",
    };
  }

  if (archiveExt.has(ext)) {
    return {
      Icon: FileArchive,
      iconColor: "#7c3aed",
      badgeBg: "#ede9fe",
      badgeText: ext.toUpperCase(),
    };
  }

  if (codeExt.has(ext)) {
    return {
      Icon: FileCode,
      iconColor: "#0f766e",
      badgeBg: "#ccfbf1",
      badgeText: ext.toUpperCase(),
    };
  }

  return {
    Icon: FileText,
    iconColor: "#7c3aed",
    badgeBg: "#f3e8ff",
    badgeText: ext ? ext.toUpperCase() : "FILE",
  };
};

const FilePills = ({ files, emptyLabel }) => {
  if (!files.length) {
    return <span style={{ color: "#6b7280", fontSize: 12 }}>{emptyLabel}</span>;
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {files.map((fileName) => {
        const fileUrl = apiUrl.downloadTool(fileName);
        const { Icon, iconColor } = getFileVisual(fileName);

        return (
          <div
            key={fileName}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              border: "1px solid #e5e7eb",
              borderRadius: 999,
              padding: "6px 10px",
              background: "#fff",
            }}
          >
            <Icon size={14} color={iconColor} />
            {/* <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#374151",
                background: badgeBg,
                borderRadius: 999,
                padding: "2px 6px",
                lineHeight: 1.4,
              }}
            >
              {badgeText}
            </span> */}
            <span style={{ fontSize: 12, color: "#111827", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={fileName}>
              {fileName}
            </span>
            
            <a
              href={fileUrl}
              download
              title="Download file"
              style={{ color: "#16a34a", display: "inline-flex" }}
            >
              <Download size={14} />
            </a>
          </div>
        );
      })}
    </div>
  );
};

const ToolUploadedDocuments = () => {
  const [tools, setTools] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deletingFile, setDeletingFile] = useState("");
  const [pendingDeleteFile, setPendingDeleteFile] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState("");
  const perPage = 10;

  const loadTools = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiService.get(apiUrl.uploadedToolDocuments());
      const rows = response?.data || [];
      setTools(rows || []);
    } catch (err) {
      console.error("Failed to load uploaded documents", err);
      setError("Unable to load uploaded documents. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTools();
  }, []);

  const openDeleteModal = (fileName) => {
    if (!fileName) {
      return;
    }
    setPendingDeleteFile(fileName);
    setShowDeleteModal(true);
  };

  const handleDeleteFile = async () => {
    if (!pendingDeleteFile) {
      return;
    }

    try {
      setDeletingFile(pendingDeleteFile);
      await apiService.delete(apiUrl.uploadedToolDocumentByName(pendingDeleteFile));
      await loadTools();
      setShowDeleteModal(false);
      setPendingDeleteFile("");
    } catch (err) {
      console.error("Failed to delete uploaded document", err);
      const message = err?.response?.data?.error || "Failed to delete file.";
      setError(message);
    } finally {
      setDeletingFile("");
    }
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return (tools || [])
      .map((tool) => ({
        ...tool,
        productFiles: parseFileList(tool.filePath),
        allFiles: [...new Set([...parseFileList(tool.filePath), ...parseFileList(tool.onboardingFilePath)])],
      }))
      .filter((tool) => tool.allFiles.length)
      .filter((tool) => {
        if (!q) {
          return true;
        }

        const mergedNames = tool.allFiles
          .join(" ")
          .toLowerCase();

        return (
          String(tool.toolName || "").toLowerCase().includes(q) ||
          String(tool.tooltype || "").toLowerCase().includes(q) ||
          mergedNames.includes(q)
        );
      });
  }, [tools, search]);

  const totalPages = Math.max(1, Math.ceil(rows.length / perPage));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return rows.slice(start, start + perPage);
  }, [rows, page]);

  const columns = [
    {
      key: "toolName",
      label: "Tool",
      cellStyle: { minWidth: 180, fontWeight: 600, color: "#1f2937" },
      render: (row) => row.toolName || "-",
    },
    {
      key: "tooltype",
      label: "Type",
      cellStyle: { minWidth: 160, color: "#4b5563" },
      render: (row) => row.tooltype || "-",
    },
    {
      key: "allFiles",
      label: "Documents",
      cellStyle: { minWidth: 260 },
      render: (row) => (
      <FilePills files={row.allFiles} emptyLabel="No documents" />
     ),
    },
    {
      key: "actions",
      label: "Action",
      cellStyle: { minWidth: 120 },
      render: (row) => {
        const candidate = row.allFiles?.[0] || "";
        const isDeleting = deletingFile === candidate;
        return (
          <button
            type="button"
            onClick={() => openDeleteModal(candidate)}
            disabled={!candidate || isDeleting}
            title={candidate ? `Delete ${candidate}` : "No file to delete"}
            style={{
              border: "1px solid #ef4444",
              background: isDeleting ? "#fee2e2" : "#fff",
              color: "#b91c1c",
              borderRadius: 6,
              padding: "6px 10px",
              fontSize: 12,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              cursor: !candidate || isDeleting ? "not-allowed" : "pointer",
              opacity: !candidate ? 0.5 : 1,
            }}
          >
            <Trash2 size={14} />
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        );
      },
    },
  ];

  return (
    <div
      style={{
        borderRadius: 8,
        padding: 32,
        minHeight: "calc(100vh - 124px)",
        boxSizing: "border-box",
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        display: "block",
        background: "rgba(187, 28, 236, 0.05)",
      }}
      className="bg-[#bb1ccc]/5"
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, color: "#1f2937", fontSize: 22 }}>Uploaded Documents</h2>
          <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 13 }}>
            Files uploaded during tool registration.
          </p>
        </div>

        <div style={{ position: "relative" }}>
          <Search size={16} color="#6b7280" style={{ position: "absolute", left: 10, top: 10 }} />
          <input
            type="text"
            value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            placeholder="Search by tool, type, or file name"
            style={{
              width: 320,
              maxWidth: "100%",
              padding: "8px 10px 8px 34px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              outline: "none",
              fontSize: 13,
              background: "#fff",
            }}
          />
        </div>
      </div>

      <div style={{ marginBottom: 12, fontSize: 13, color: "#4b5563" }}>
        Total tools with documents: <strong>{rows.length}</strong>
      </div>

      {!error && (
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 13, color: "#6b7280" }}>
            Showing {rows.length === 0 ? 0 : (page - 1) * perPage + 1}-{Math.min(page * perPage, rows.length)} of {rows.length}
          </span>
          <PaginationControls
            page={page}
            totalPages={totalPages}
            isLoading={loading}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        </div>
      )}

      {error && (
        <div style={{ padding: 20, background: "#fff", borderRadius: 8, border: "1px solid #fecaca", color: "#b91c1c" }}>
          {error}
        </div>
      )}

      {!error && (
        <div style={{ overflowX: "auto", background: "white", borderRadius: 10, border: "1px solid #e5e7eb" }}>
          <DataTable
            columns={columns}
              rows={paginatedRows}
            loading={loading}
            skeletonRowCount={5}
            emptyMessage={<span style={{ color: "#999", fontSize: "15px" }}>No uploaded files found for tool registrations.</span>}
            rowKey={(row, idx) => row.id ?? row.toolName ?? `tool-doc-row-${idx}`}
            loadingRow={(idx) => (
              <TableSkeletonRow
                idx={idx}
                rowKeyPrefix="uploaded-docs-skeleton"
                columnCount={columns.length}
                actionColumnIndex={columns.length - 1}
                actionSkeletonCount={1}
                cellWidths={["8rem", "7rem", "12rem", "12rem", "6rem"]}
              />
            )}
            rowProps={(_, idx) => ({
              style: {
                background: idx % 2 === 0 ? "#ffffff" : "#f8f5fb",
                borderRadius: "8px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                border: "1px solid #e5e5e5",
                transition: "all 0.3s ease",
              },
              onMouseEnter: (e) => {
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(187,28,204,0.15)";
                e.currentTarget.style.transform = "translateY(-2px)";
              },
              onMouseLeave: (e) => {
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
                e.currentTarget.style.transform = "translateY(0)";
              },
            })}
          />
        </div>
      )}

      {showDeleteModal && (
        <DeleteModal
          title={"Are you sure you want to delete this file?"}
          onClose={() => {
            if (!deletingFile) {
              setShowDeleteModal(false);
              setPendingDeleteFile("");
            }
          }}
          onConfirm={handleDeleteFile}
          isLoading={Boolean(deletingFile)}
        />
      )}
    </div>
  );
};

export default ToolUploadedDocuments;
